const mapMaterialKindToOption = (material?: Material) => {
  switch (material?.kind) {
    case 'Softwood':
      return 'softwoodMaterial'
    case 'Glulam':
      return 'glulamMaterial'
    default:
      return 'lvlMaterial'
  }
}

const withDefaultUsageClass = <T extends CrossSection | null | undefined>(crossSection: T): T => {
  if (!crossSection) return crossSection
  return {
    ...crossSection,
    usage_class: crossSection.usage_class ?? 'One',
  }
}

const normalizeInsulation = <
  T extends { insulation: Product | null; insulation_thickness: number | null },
>(
  payload: T,
  hasInsulation: boolean,
) => ({
  insulation: hasInsulation ? payload.insulation : null,
  insulation_thickness: hasInsulation ? payload.insulation_thickness : null,
})

export const formStateToLayer = (
  values: TimberFrameLayerFormState,
  variant: TimberFrameLayerVariant,
): TimberFrameLayer => {
  if (variant === 'roof-ceiling') {
    const { crossSectionStandardBeam, hasInsulation, insulation, insulation_thickness, ...rest } =
      values.layer as TimberFrameLayerRoofCeilingFormState['layer']

    const normalized = normalizeInsulation({ insulation, insulation_thickness }, hasInsulation)

    return {
      ...rest,
      kind: 'timber-frame-layer-roof-ceiling',
      standard_beam_cs: withDefaultUsageClass(crossSectionStandardBeam),
      ...normalized,
    }
  }

  const {
    crossSectionStandardRip,
    crossSectionLintel,
    crossSectionTopChord,
    crossSectionBottomChord,
    hasInsulation,
    insulation,
    insulation_thickness,
    ...rest
  } = values.layer as TimberFrameLayerWallFormState['layer']

  const normalized = normalizeInsulation({ insulation, insulation_thickness }, hasInsulation)

  return {
    ...rest,
    kind: 'timber-frame-layer-wall',
    standard_rip_cs: withDefaultUsageClass(crossSectionStandardRip),
    lintel_cs: withDefaultUsageClass(crossSectionLintel),
    top_chord_cs: withDefaultUsageClass(crossSectionTopChord),
    bottom_chord_cs: withDefaultUsageClass(crossSectionBottomChord),
    ...normalized,
  }
}

export const layerToFormState = (
  layer: TimberFrameLayer,
  variant: TimberFrameLayerVariant,
): TimberFrameLayerFormState => {
  const isRoofCeilingLayer =
    variant === 'roof-ceiling' || layer.kind === 'timber-frame-layer-roof-ceiling'

  if (isRoofCeilingLayer) {
    const { standard_beam_cs, insulation, insulation_thickness, ...rest } =
      layer as TimberFrameLayerRoofCeiling

    const hasInsulation = Boolean(insulation)

    return {
      materialTypeStandardBeam: mapMaterialKindToOption(standard_beam_cs?.material),
      layer: {
        ...rest,
        kind: 'timber-frame-layer-roof-ceiling',
        crossSectionStandardBeam: withDefaultUsageClass(standard_beam_cs),
        hasInsulation,
        insulation: hasInsulation ? insulation : null,
        insulation_thickness: hasInsulation ? insulation_thickness : null,
      },
    }
  }

  const {
    standard_rip_cs,
    lintel_cs,
    top_chord_cs,
    bottom_chord_cs,
    insulation,
    insulation_thickness,
    ...rest
  } = layer as TimberFrameLayerWall

  const hasInsulation = Boolean(insulation)

  return {
    materialTypeStandardRip: mapMaterialKindToOption(standard_rip_cs?.material),
    materialTypeLintel: mapMaterialKindToOption(lintel_cs?.material),
    materialTypeTopChord: mapMaterialKindToOption(top_chord_cs?.material),
    materialTypeBottomChord: mapMaterialKindToOption(bottom_chord_cs?.material),
    layer: {
      ...rest,
      kind: 'timber-frame-layer-wall',
      crossSectionStandardRip: withDefaultUsageClass(standard_rip_cs),
      crossSectionLintel: withDefaultUsageClass(lintel_cs),
      crossSectionTopChord: withDefaultUsageClass(top_chord_cs),
      crossSectionBottomChord: withDefaultUsageClass(bottom_chord_cs),
      hasInsulation,
      insulation: hasInsulation ? insulation : null,
      insulation_thickness: hasInsulation ? insulation_thickness : null,
    },
  }
}
